import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import type { ListData } from '@openx/types/inventoryList';
import { copyToClipboard } from '@openx/utils/lib/copyToClipboard';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CopyToClipboardAction } from './components/CopyToClipboardAction';

interface InvalidItemsProps {
  handleBulkReplace: (items: string[]) => void;
  items: string[];
  listData: ListData[];
  limit: number;
}

export const ItemsOverLimit = (props: InvalidItemsProps) => {
  const { handleBulkReplace, items, listData, limit } = props;
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const nonValidItems = items.length + listData.length - limit;
  const itemText = nonValidItems > 1 ? t('items') : t('item');

  const onMoveToClipboardClick = useCallback(() => {
    copyToClipboard(items.slice(items.length - nonValidItems).join('\n'))
      .then(() => {
        handleBulkReplace(items.slice(0, items.length - nonValidItems));

        enqueueSnackbar(t('{num} {itemText} moved to your clipboard', { itemText, num: nonValidItems }), {
          variant: 'success',
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center',
          },
        });
      })
      .catch(() => {
        enqueueSnackbar(t('Failed to move items to your clipboard'), {
          variant: 'error',
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center',
          },
        });
      });
  }, [items, handleBulkReplace, t, nonValidItems, enqueueSnackbar, itemText]);

  return (
    <Alert
      severity="error"
      action={<CopyToClipboardAction onClick={onMoveToClipboardClick} />}
      data-test="items-over-limit-bulk-message"
      sx={{
        width: '100%',
        mr: 1,
        alignItems: 'center',
      }}
    >
      <Typography variant="body1">
        <b>{t('Remove {num} {itemText} to continue', { itemText, num: nonValidItems })}</b>
      </Typography>
    </Alert>
  );
};
